import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { DashboardTile } from './DashboardTile';
import { tokens } from '../../../design';

/**
 * Permission-gate fallback for an analytics tile. Renders in the SAME grid
 * slot as the real tile (so the layout stays intact for admins without
 * `view_analytics`) with the tile label + a muted lock chip. Used as the
 * `<RequirePermission fallback>` for each analytics tile — never shows a
 * fabricated number.
 */
export interface LockedTileProps {
  label: string;
  gridColumn: string;
  gridRow?: string;
}

export const LockedTile = ({ label, gridColumn, gridRow }: LockedTileProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <DashboardTile sx={{ gridColumn, gridRow }}>
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          color: tokens.text.tertiary,
          mb: 1.5,
        }}
      >
        {label}
        <Typography
          component="span"
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'none',
            letterSpacing: 0,
            padding: '2px 8px',
            borderRadius: 999,
            color: tokens.text.tertiary,
            background: tokens.surface.muted,
          }}
        >
          {t('dashboard.locked')}
        </Typography>
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: tokens.text.tertiary, fontSize: 12, my: 'auto' }}
      >
        {t('dashboard.lockedBody')}
      </Typography>
    </DashboardTile>
  );
};
